import { useState, useMemo } from "react";
import { Search, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/Button";
import { QuoteCTA } from "@/components/QuoteCTA";
import { siteConfig } from "@/data/siteConfig";
import { useSiteContent } from "@/context/ContentContext";

export function CatalogueView() {
  const { contact } = useSiteContent();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [model, setModel] = useState("All");

  const categories = ["All", ...Array.from(new Set(siteConfig.products.map((p) => p.category)))];
  const models = ["All", ...Array.from(new Set(siteConfig.products.map((p) => p.model)))];

  const filtered = useMemo(() => siteConfig.products.filter((p) => {
    const matchesQ = !q || (p.part + p.category + p.model + p.desc).toLowerCase().includes(q.toLowerCase());
    return matchesQ && (cat === "All" || p.category === cat) && (model === "All" || p.model === model);
  }), [q, cat, model]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16 lg:py-24">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-10">
        <div className="relative sm:col-span-2 lg:col-span-2">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search part number, model or keyword…" data-testid="catalogue-search"
            className="w-full border border-[#E3E8EF] bg-white py-3.5 pl-11 pr-4 text-sm outline-none transition-colors focus:border-ink focus-ring" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="catalogue-category-filter" className="border border-[#E3E8EF] bg-white py-3.5 px-4 text-sm outline-none focus:border-ink focus-ring">
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="catalogue-model-filter" className="border border-[#E3E8EF] bg-white py-3.5 px-4 text-sm outline-none focus:border-ink focus-ring">
          {models.map((m) => <option key={m}>{m === "All" ? "All Models" : m}</option>)}
        </select>
      </div>

      <div className="mb-6">
        <span className="text-sm text-muted">{filtered.length} part{filtered.length === 1 ? "" : "s"} available</span>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-[#CBD3DD] bg-canvas p-16 text-center">
          <p className="text-muted">No parts match your search. Send us the details and we'll help you find it.</p>
          <div className="mt-6 flex justify-center"><Button to="/request-a-quote" variant="accent" arrow>Request a Quote</Button></div>
        </div>
      ) : (
        <>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => <ProductCard key={p.part} item={p} index={i} testid={`product-card-${i}`} />)}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-5 border border-[#E3E8EF] bg-canvas px-6 sm:px-8 py-7" data-testid="more-parts-cta">
            <div>
              <h3 className="font-display uppercase text-lg tracking-wide text-ink">Looking for Something Else?</h3>
              <p className="mt-1 text-sm text-muted max-w-lg">We carry many more tractor, implement, backhoe loader and linkage parts beyond what's shown here. Send us your part number or requirement and we'll check availability.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full sm:w-auto">
              <a href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent("Hi, I'm looking for a part that's not listed in your parts catalogue. Can you help?")}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-ink/25 px-6 py-3 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-white"
                data-testid="more-parts-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
              <Button to="/request-a-quote" variant="accent" arrow data-testid="more-parts-enquiry-btn">Enquire for More Parts</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Parts() {
  return (
    <>
      <PageHero kicker="Parts Catalogue" title="Tractor, Implement & Backhoe Loader Parts"
        text="Explore our available component categories — Tractor Parts, Agricultural Implement Parts, Backhoe Loader Parts and 3-Point Linkage Parts — or send your part details directly for a quotation."
        image="https://images.unsplash.com/photo-1570410147780-c3c9789ed623?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />
      <CatalogueView />
      <QuoteCTA />
    </>
  );
}
